import { Component, inject, Input, OnChanges, OnDestroy, SimpleChange, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { Subject } from 'rxjs';
import { CriticalAlertResponse } from '../../models/maintenance-overview-response.model';
import { EmptyStateComponent } from '../../../../../_shared/components/states/empty-state/empty-state.component';
import { ErrorStateComponent } from '../../../../../_shared/components/states/error-state/error-state.component';
import { ServiceTypes } from '../../../../_shared/enums/service-type.enum';
import { CriticalAlertsPaginationRequest } from '../../models/maintenance-overview-request.model';
import { MaintenanceOverviewService } from '../../services/maintenance-overview.service';
import { ErrorHandlerService } from '../../../../../_shared/services/error-handler.service';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-critical-alerts',
  standalone: true,
  imports: [CommonModule, EmptyStateComponent, ErrorStateComponent, InfiniteScrollModule, TranslocoDirective],
  templateUrl: './critical-alerts.component.html',
  styleUrl: './critical-alerts.component.scss'
})
export class CriticalAlertsComponent implements OnChanges, OnDestroy {
  @Input() accountIds: number[] = [];
  @Input() searchTerm: string = '';

  alerts: CriticalAlertResponse[] = [];
  isLoading: boolean = true;
  hasError: boolean = false;
  page: number = 1;
  pageSize: number = 20;
  totalPages: number = 0;
  totalCount: number = 0;

  private transloco = inject(TranslocoService);

  serviceTypeLabels: Record<ServiceTypes, string> = {
    [ServiceTypes.Minor]: this.transloco.translate('maintenanceOverview.serviceType.minor'),
    [ServiceTypes.Intermediate]: this.transloco.translate('maintenanceOverview.serviceType.intermediate'),
    [ServiceTypes.Major]: this.transloco.translate('maintenanceOverview.serviceType.major')
  };

  private maintenanceService = inject(MaintenanceOverviewService);
  private errorService = inject(ErrorHandlerService);
  private cancelCriticalAlerts = new Subject<void>();

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['searchTerm'] && !changes['accountIds']) {
      return;
    }
    this.resetPagination();
    this.loadCriticalAlerts();
  }

  ngOnDestroy(): void {
    this.cancelCriticalAlerts.next();
    this.cancelCriticalAlerts.complete();
  }

  formatMaintenanceReason(item: CriticalAlertResponse): string {
    return item.isAutoTrigger
      ? this.serviceTypeLabels[item.serviceType]
      : item.localizedMaintenanceReason || item.maintenanceReason;
  }

  formatAssignedTo(item: CriticalAlertResponse): string {
    const assignedTo = item.assignedTo?.trim();
    return assignedTo
      ? this.transloco.translate('maintenanceOverview.criticalAlerts.assignedTo', { name: assignedTo })
      : this.transloco.translate('maintenanceOverview.criticalAlerts.noTechnician');
  }

  hasAssignedTechnician(item: CriticalAlertResponse): boolean {
    return !!item.assignedTo?.trim();
  }

  formatDue(due: number): string {
    if (due < 0) {
      const days = Math.abs(due);
      return this.transloco.translate(
        days === 1 ? 'maintenanceOverview.criticalAlerts.overdueByDay' : 'maintenanceOverview.criticalAlerts.overdueByDays',
        { count: days }
      );
    }

    if (due === 0) return this.transloco.translate('maintenanceOverview.criticalAlerts.today');
    if (due < 7) {
      return this.transloco.translate(
        due === 1 ? 'maintenanceOverview.criticalAlerts.dueInDay' : 'maintenanceOverview.criticalAlerts.dueInDays',
        { count: due }
      );
    }
    if (due < 30) {
      const weeks = Math.floor(due / 7);
      return this.transloco.translate(
        weeks === 1 ? 'maintenanceOverview.criticalAlerts.dueInWeek' : 'maintenanceOverview.criticalAlerts.dueInWeeks',
        { count: weeks }
      );
    }

    const months = Math.floor(due / 30);
    return this.transloco.translate(
      months === 1 ? 'maintenanceOverview.criticalAlerts.dueInMonth' : 'maintenanceOverview.criticalAlerts.dueInMonths',
      { count: months }
    );
  }

  onScrollDown(): void {
    if (this.isLoading || this.page >= this.totalPages) return;
    this.page++;
    this.loadCriticalAlerts();
  }

  async loadCriticalAlerts(): Promise<void> {
    this.hasError = false;
    this.isLoading = true;

    try {
      const request: CriticalAlertsPaginationRequest = {
        accountIds: this.accountIds ?? [],
        searchTerm: this.searchTerm,
        page: this.page,
        pageSize: this.pageSize
      };

      const response = await this.maintenanceService.getCriticalAlerts(request, this.cancelCriticalAlerts);
      const pagination = response.data;
      const newAlerts = pagination?.results ?? [];

      this.alerts = this.page === 1
        ? newAlerts
        : [...this.alerts, ...newAlerts];
      this.totalPages = pagination?.totalPages ?? 0;
      this.totalCount = pagination?.totalCount ?? 0;

      this.isLoading = false;
    } catch (error) {
      this.errorService.handleError(error, {
        callback: () => {
          this.hasError = true;
          this.isLoading = false;
        }
      });
    }
  }

  private resetPagination(): void {
    this.cancelCriticalAlerts.next();
    this.page = 1;
    this.totalPages = 0;
    this.totalCount = 0;
    this.alerts = [];
  }
}